/**
 * Table de correspondance entre les caractères de format de date PHP (renvoyés par
 * l'API dans `default_date_format`) et les tokens du `DatePipe` d'Angular.
 *
 * Les entrées vides correspondent aux caractères PHP sans équivalent Angular :
 * ils sont simplement retirés du format.
 */
const PHP_TO_ANGULAR_TOKENS: Record<string, string> = {
  // Jour
  d: 'dd',
  j: 'd',
  D: 'EEE',
  l: 'EEEE',
  N: 'E',
  w: 'E',
  S: '',
  z: '',
  // Semaine
  W: 'w',
  // Mois
  F: 'MMMM',
  M: 'MMM',
  m: 'MM',
  n: 'M',
  t: '',
  // Année
  L: '',
  o: 'Y',
  Y: 'y',
  y: 'yy',
  // Heure
  a: 'a',
  A: 'a',
  g: 'h',
  h: 'hh',
  G: 'H',
  H: 'HH',
  i: 'mm',
  s: 'ss',
  u: '',
  v: 'SSS',
  // Fuseau
  e: 'zzzz',
  T: 'z',
  P: 'ZZZZZ',
  O: 'Z',
  Z: '',
  U: '',
};

const isLetter = (char: string): boolean => /[a-zA-Z]/.test(char);

/**
 * Convertit un format de date PHP en format compatible avec le `DatePipe` d'Angular.
 *
 * Sans cette conversion, des caractères communs aux deux syntaxes mais de sens
 * différent sont mal interprétés : `m` vaut le mois en PHP et les minutes en Angular,
 * `d` vaut le jour sur 2 chiffres en PHP et sur 1 ou 2 chiffres en Angular, etc.
 *
 * Les caractères littéraux sont échappés par des quotes simples, comme attendu par Angular.
 *
 * @example phpToAngularDateFormat('d/m/Y H:i') // => 'dd/MM/y HH:mm'
 */
export function phpToAngularDateFormat(phpFormat: string): string {
  let angularFormat: string = '';
  let literals: string = '';

  const flushLiterals = (): void => {
    if (!literals) return;
    angularFormat += isLetter(literals) ? `'${literals.replace(/'/g, "''")}'` : literals;
    literals = '';
  };

  for (let index: number = 0; index < phpFormat.length; index++) {
    const char: string = phpFormat[index];

    if (char === '\\') {
      index++;
      if (index < phpFormat.length) literals += phpFormat[index];
      continue;
    }

    const token: string | undefined = PHP_TO_ANGULAR_TOKENS[char];
    if (token === undefined) {
      literals += char;
      continue;
    }

    flushLiterals();
    angularFormat += token;
  }

  flushLiterals();

  return angularFormat;
}
